/**
 * The plasma etch simulation: the two scales joined.
 *
 * Order of solution each time the recipe moves:
 *
 *   1. MICRO   sheath.ts solves the discharge -> ion flux, ion energy
 *   2. MACRO   ReactorSolver transports feed and radical to steady state
 *   3. SURFACE kinetics.ts competes the two supplies at each radius
 *   4. FILM    thickness integrates on the slow clock
 *
 * Step 3 feeds back into step 2 (consumption is a sink on the radical field),
 * which is why the transport solver Picard iterates rather than solving once.
 * The centre-to-edge signature and the loading effect both emerge from that
 * loop; neither is written anywhere as a rule.
 */

import { FILM_DENSITY, MASS, mtorrToPa, numberDensity, diffusivity } from './constants'
import { CHAMBER, buildFlow, etchantThermalSpeed, type Geometry } from './flow'
import { ReactorSolver } from './ReactorSolver'
import {
  gasTemperature,
  impingementFlux,
  ionEnergyDistribution,
  solvePlasma,
  type PlasmaState,
} from './sheath'
import { profileAngleDeg, solveSurface, toNmPerMin, type SurfaceResult } from './kinetics'
import { NOMINAL_CHAMBER, type ChamberState, type Recipe, DEFAULT_RECIPE, cloneRecipe,
  effectiveBasePressure,
  effectiveChuckOffset,
  effectiveLeakUp,
  effectiveSourceMatch,
} from './recipe'
import { clamp, mean, nonUniformityPct, sigmaPct } from '../core/math'

/** Starting polysilicon thickness [m]. */
export const INITIAL_FILM_THICKNESS = 500e-9

export interface RadialSample {
  /** Radius [m]. */
  radius: number
  /** Normalised radius, 0 at centre, 1 at wafer edge. */
  x: number
  /** Remaining film thickness [m]. */
  thickness: number
  /** Instantaneous etch rate [m/s]. */
  etchRate: number
  /** Radical density at the surface [m^-3]. */
  radicalDensity: number
  /** Ion flux at this radius [m^-2 s^-1]. */
  ionFlux: number
  /** Oxide loss once the film has cleared here [m]. */
  oxideLoss: number
  /** True once the polysilicon has cleared at this radius. */
  cleared: boolean
  surface: SurfaceResult
}

export interface EtchMetrics {
  /** Mean etch rate across the wafer [nm/min]. */
  meanRateNmMin: number
  /** Half-range non-uniformity of the rate [%]. */
  rateNonUniformityPct: number
  /** 1-sigma non-uniformity of remaining thickness [%]. */
  thicknessSigmaPct: number
  /** Positive when the edge etches faster than the centre. */
  edgeMinusCentrePct: number
  /** Si:SiO2 selectivity at wafer centre. */
  selectivity: number
  /** Sidewall angle [deg from horizontal]; 90 is perfectly anisotropic. */
  profileAngleDeg: number
  /** Area-averaged Damkoehler number: >1 means transport-limited. */
  damkohler: number
  /** Worst-case oxide punch-through anywhere on the wafer [m]. */
  maxOxideLoss: number
  /** Fraction of the wafer that has cleared. */
  clearedFraction: number
  /** Elapsed process time [s]. */
  processTime: number
  /** Gas residence time [s]. */
  residenceTime: number
  /** Feed dissociation fraction at the wafer centre. */
  dissociationFraction: number
  /** Final relative residual of the transport solve. */
  residual: number
}

export interface EndpointPoint {
  t: number
  /** Normalised optical emission from the etch product. */
  signal: number
}

/**
 * Radial shape of the ion flux. Inductive sources are centre-peaked; the focus
 * ring shapes the field at the wafer edge. Normalised inside the class so the
 * area average matches the discharge solution exactly.
 *
 * `ringWear` is why a focus ring is a consumable. A new ring holds the edge
 * near the rest of the wafer; an eroded one stops confining the field and edge
 * flux runs away. Because this is a pure SHAPE change, the area average is
 * unchanged - the mean etch rate barely moves and only a uniformity map catches
 * it. That is exactly how the fault behaves on a real tool, and why a rate
 * control chart alone will miss it.
 */
function ionFluxShape(x: number, ringWear = 0): number {
  const edge = 0.09 + 1.5 * Math.max(ringWear - 0.4, 0)
  return 1.04 - 0.12 * x * x + edge * Math.pow(x, 8)
}

export class EtchSim {
  readonly geom: Geometry
  readonly solver: ReactorSolver

  recipe: Recipe
  chamber: ChamberState

  private readonly nR: number
  private readonly thickness: Float64Array
  private readonly oxideLoss: Float64Array
  private readonly surfaceDensity: Float64Array
  private readonly ionFluxRadial: Float64Array
  private readonly rateRadial: Float64Array
  private readonly areaWeight: Float64Array
  private surfaceResults: SurfaceResult[] = []

  private plasma!: PlasmaState
  private gasTempK = 320
  private waferTempK = 313
  private inletFeedDensity = 0
  private residual = 1
  private processTime = 0
  private dirty = true

  /** Optical emission endpoint trace. */
  readonly endpointTrace: EndpointPoint[] = []

  constructor(geom: Geometry = CHAMBER) {
    this.geom = geom
    this.solver = new ReactorSolver(geom, 32, 16)
    this.recipe = cloneRecipe(DEFAULT_RECIPE)
    this.chamber = { ...NOMINAL_CHAMBER }

    this.nR = this.solver.waferCellCount
    this.thickness = new Float64Array(this.nR).fill(INITIAL_FILM_THICKNESS)
    this.oxideLoss = new Float64Array(this.nR)
    this.surfaceDensity = new Float64Array(this.nR)
    this.ionFluxRadial = new Float64Array(this.nR)
    this.rateRadial = new Float64Array(this.nR)
    this.areaWeight = new Float64Array(this.nR)

    // Area weights for honest wafer-area averaging: an annulus at large r
    // carries far more area than one near the centre.
    let total = 0
    for (let i = 0; i < this.nR; i++) {
      const w = this.solver.radiusAt(i)
      this.areaWeight[i] = w
      total += w
    }
    for (let i = 0; i < this.nR; i++) this.areaWeight[i] /= total

    this.markDirty()
    this.recompute(9000)
    this.evaluateSurface()
  }

  /** Flag the physics as stale; the next step will re-derive it. */
  markDirty(): void {
    this.dirty = true
  }

  setRecipe(patch: Partial<Recipe>): void {
    this.recipe = { ...this.recipe, ...patch }
    this.markDirty()
  }

  setChamber(patch: Partial<ChamberState>): void {
    this.chamber = { ...this.chamber, ...patch }
    this.markDirty()
  }

  /** Fires when a fresh wafer is loaded, so callers can reset per-wafer state. */
  onWaferLoaded: (() => void) | null = null

  /** Load a fresh wafer and restart the process clock. */
  loadWafer(): void {
    this.thickness.fill(INITIAL_FILM_THICKNESS)
    this.oxideLoss.fill(0)
    this.processTime = 0
    this.endpointTrace.length = 0
    this.markDirty()
    this.onWaferLoaded?.()
  }

  get plasmaState(): PlasmaState {
    return this.plasma
  }

  get elapsed(): number {
    return this.processTime
  }

  get radialCount(): number {
    return this.nR
  }

  ionEnergySpectrum(bins = 64) {
    return ionEnergyDistribution(this.plasma, bins)
  }

  /**
   * Re-derive plasma and transport. Expensive relative to a frame, so it runs
   * only when something actually changed.
   */
  private recompute(sweeps: number): void {
    const rec = this.recipe
    const electrodeArea = Math.PI * this.geom.waferRadius ** 2

    this.gasTempK = gasTemperature(rec.sourcePowerW, rec.pressureMtorr)
    // The chuck reads its setpoint; the wafer is at the setpoint plus whatever
    // the chiller is failing to remove.
    this.waferTempK = rec.wafertempC + effectiveChuckOffset(this.chamber) + 273.15

    // --- 1. MICROSCOPIC: solve the discharge ---
    this.plasma = solvePlasma({
      pressureMtorr: rec.pressureMtorr,
      sourcePowerW: rec.sourcePowerW,
      biasPowerW: rec.biasPowerW,
      gasTempK: this.gasTempK,
      electrodeAreaM2: electrodeArea,
      sourceMatchEfficiency: effectiveSourceMatch(this.chamber),
      biasMatchEfficiency: this.chamber.biasMatchEfficiency,
    })

    // Ion flux profile, renormalised so its area average is the solved value.
    let shapeAvg = 0
    for (let i = 0; i < this.nR; i++) {
      const x = this.solver.radiusAt(i) / this.geom.waferRadius
      shapeAvg += ionFluxShape(x, this.chamber.focusRingWear) * this.areaWeight[i]
    }
    for (let i = 0; i < this.nR; i++) {
      const x = this.solver.radiusAt(i) / this.geom.waferRadius
      this.ionFluxRadial[i] =
        (this.plasma.ionFlux * ionFluxShape(x, this.chamber.focusRingWear)) / shapeAvg
    }

    // --- 2. MACROSCOPIC: transport ---
    const pressurePa = mtorrToPa(rec.pressureMtorr + effectiveBasePressure(this.chamber))
    const totalDensity = numberDensity(pressurePa, this.gasTempK)

    // A leak admits air, which displaces feed from the mixture. The CF4 the
    // wafer sees is the fraction of a chamber that is not full of atmosphere.
    // Air is air: the chamber cannot tell a leak in its own door seal from a
    // leak in the header it hangs off, and neither can the physics.
    const leakUp = effectiveLeakUp(this.chamber)
    const leakFraction = clamp(leakUp / 120, 0, 0.6)
    this.inletFeedDensity = totalDensity * rec.cf4Fraction * (1 - leakFraction)

    // The mass flow controller delivers what it delivers, not what it reads.
    const deliveredFlow = rec.flowSccm * this.chamber.flowDelivery

    const flow = buildFlow({
      geom: this.geom,
      flowSccm: deliveredFlow,
      pressureMtorr: rec.pressureMtorr,
      gasTempK: this.gasTempK,
    })

    const D = diffusivity(pressurePa, this.gasTempK)
    const vth = etchantThermalSpeed(this.gasTempK)

    // Electron-impact dissociation of CF4. Rate coefficient is the usual
    // Arrhenius-in-Te form with a ~12 eV threshold.
    //
    // The prefactor is set so the baseline runs at roughly half feed
    // utilisation. That matters pedagogically: near 100% utilisation every F
    // atom the plasma can make is already being made, the chamber is gas
    // starved, and pressure stops doing anything because the sccm setpoint caps
    // the fluorine budget. Sitting at ~50% leaves the tool responsive to both
    // pressure and flow, and lets a student drive it INTO starvation on purpose
    // by closing the mass flow controller down.
    const rateCoeff = 1.2e-15 * Math.exp(-12 / this.plasma.electronTempEv)
    const dissociationRate = this.plasma.plasmaDensity * rateCoeff
    ;(this.solver as unknown as Record<string, number>).lastDissRate = dissociationRate

    // Seasoned walls recombine radicals more readily than clean ones - which is
    // why the first wafer after a chamber clean etches differently.
    const wallRecombination = 0.002 + 0.02 * this.chamber.wallSeasoning

    // Oxygen from a leak scavenges fluorine in the gas phase. The coefficient
    // is calibrated so a leak at the 1 mTorr/min PM limit is negligible while a
    // frank leak of tens of mTorr/min visibly starves the etch - which matches
    // why that limit is where it is.
    const etchantVolumeLoss = 5.5 * Math.max(leakUp - 0.5, 0)

    this.solver.setParams({
      flow,
      diffusivity: D,
      inletFeedDensity: this.inletFeedDensity,
      dissociationRate,
      etchantYield: 1.6,
      wallRecombination,
      etchantVolumeLoss,
      thermalSpeed: vth,
      surfaceSink: (i, density) => {
        if (i >= this.nR) return 0
        const neutralFlux = impingementFlux(density, this.gasTempK, MASS.F)
        const res = solveSurface({
          neutralFlux,
          ionFlux: this.ionFluxRadial[i],
          ionEnergyEv: this.plasma.ionEnergyEv,
          surfaceTempK: this.waferTempK,
        })
        // Only open film consumes etchant. Once a radius has cleared, the
        // exposed oxide consumes far less - which is itself a signal.
        const openness = this.thickness[i] > 0 ? rec.openAreaFraction : rec.openAreaFraction * 0.12
        return res.etchantConsumption * openness
      },
    })

    if (this.processTime === 0) this.solver.seed(this.inletFeedDensity)
    // `sweeps` is a budget, not a cost: the solver stops as soon as the residual
    // falls under tolerance. A slider nudge starts from the previous solution and
    // converges in a handful of passes; a cold start or a large pressure jump
    // genuinely needs the whole budget and gets it. Capping this to make the
    // interactive case cheap only starved the hard cases - high pressure, slow
    // diffusion - which are exactly where the answer is most convergence-sensitive.
    this.residual = this.solver.solve(sweeps)
    this.dirty = false
  }

  /**
   * Advance the process by dt seconds of wall-clock fab time.
   *
   * The transport field is re-relaxed a little each call so it tracks the film
   * as the load changes, then the surface is evaluated and the film integrated.
   */
  /**
   * Evaluate the surface at every radius from the current transport solution,
   * without advancing the film.
   *
   * Kept separate from `step` so the tool reports a real state before anyone
   * presses start. A recipe that is loaded but not running still has an etch
   * rate, a selectivity and a profile - those are properties of the recipe, not
   * of having pressed a button - and an HMI that shows zeros for them is
   * telling the student something false about the tool.
   */
  private evaluateSurface(): void {
    this.solver.surfaceDensity(this.surfaceDensity)
    this.surfaceResults = []
    for (let i = 0; i < this.nR; i++) {
      const density = Math.max(this.surfaceDensity[i], 0)
      const res = solveSurface({
        neutralFlux: impingementFlux(density, this.gasTempK, MASS.F),
        ionFlux: this.ionFluxRadial[i],
        ionEnergyEv: this.plasma.ionEnergyEv,
        surfaceTempK: this.waferTempK,
      })
      this.surfaceResults.push(res)
      this.rateRadial[i] = this.thickness[i] > 0 ? res.etchRate : res.oxideRate
    }
  }

  /** Bring plasma, transport and surface up to date without touching the film. */
  refresh(): void {
    if (this.dirty) this.recompute(9000)
    else this.residual = this.solver.solve(600)
    this.evaluateSurface()
  }

  step(dt: number): void {
    this.refresh()

    const rec = this.recipe
    let emission = 0

    for (let i = 0; i < this.nR; i++) {
      const res = this.surfaceResults[i]
      if (this.thickness[i] > 0) {
        // Still clearing polysilicon.
        this.thickness[i] = Math.max(this.thickness[i] - res.etchRate * dt, 0)
        // Optical emission scales with the amount of product being made.
        emission += res.etchRate * rec.openAreaFraction * this.areaWeight[i] * FILM_DENSITY.Si
      } else {
        // Cleared: the underlayer is now taking the beating. This is where poor
        // uniformity turns into real damage.
        this.oxideLoss[i] += res.oxideRate * dt
      }
    }

    this.processTime += dt

    // Normalise emission against a fresh, fully covered wafer for display.
    const reference = FILM_DENSITY.Si * 1e-8 * rec.openAreaFraction + 1e-30
    this.endpointTrace.push({ t: this.processTime, signal: clamp(emission / reference, 0, 1.5) })
    if (this.endpointTrace.length > 900) this.endpointTrace.shift()

    // No re-derive needed here. The surface sink closure reads `thickness`
    // live, so the radical field already tracks the load as the film clears -
    // the per-step relaxation above carries it. Forcing a full cold solve on a
    // timer was costing ~5 ms per frame to recompute something that had not
    // changed: plasma and transport only move when the recipe or chamber does,
    // and those paths call markDirty() themselves.
  }

  /** Per-radius state, for charts and the wafer map. */
  samples(): RadialSample[] {
    const out: RadialSample[] = []
    for (let i = 0; i < this.nR; i++) {
      const radius = this.solver.radiusAt(i)
      out.push({
        radius,
        x: radius / this.geom.waferRadius,
        thickness: this.thickness[i],
        etchRate: this.rateRadial[i],
        radicalDensity: this.surfaceDensity[i],
        ionFlux: this.ionFluxRadial[i],
        oxideLoss: this.oxideLoss[i],
        cleared: this.thickness[i] <= 0,
        surface: this.surfaceResults[i] ?? this.surfaceResults[0],
      })
    }
    return out
  }

  /** Raw thickness array, for the wafer map texture. */
  thicknessProfile(): Float64Array {
    return this.thickness
  }

  metrics(): EtchMetrics {
    const rates = Array.from(this.rateRadial)
    const thick = Array.from(this.thickness)

    const centreCount = Math.max(2, Math.round(this.nR * 0.12))
    const edgeStart = Math.round(this.nR * 0.88)
    const centreRate = mean(rates.slice(0, centreCount))
    const edgeRate = mean(rates.slice(edgeStart))

    let damk = 0
    let cleared = 0
    let maxOxide = 0
    for (let i = 0; i < this.nR; i++) {
      const s = this.surfaceResults[i]
      if (s) damk += s.damkohler * this.areaWeight[i]
      if (this.thickness[i] <= 0) cleared += this.areaWeight[i]
      if (this.oxideLoss[i] > maxOxide) maxOxide = this.oxideLoss[i]
    }

    const centre = this.surfaceResults[0]
    const feedCentre = this.solver.feed[0]
    const dissociationFraction =
      this.inletFeedDensity > 0 ? clamp(1 - feedCentre / this.inletFeedDensity, 0, 1) : 0

    const rec = this.recipe
    const flow = buildFlow({
      geom: this.geom,
      flowSccm: rec.flowSccm,
      pressureMtorr: rec.pressureMtorr,
      gasTempK: this.gasTempK,
    })

    return {
      meanRateNmMin: toNmPerMin(mean(rates)),
      rateNonUniformityPct: nonUniformityPct(rates),
      thicknessSigmaPct: sigmaPct(thick),
      edgeMinusCentrePct: centreRate > 0 ? (100 * (edgeRate - centreRate)) / centreRate : 0,
      selectivity: centre ? centre.selectivity : 0,
      profileAngleDeg: centre ? profileAngleDeg(centre) : 90,
      damkohler: damk,
      maxOxideLoss: maxOxide,
      clearedFraction: cleared,
      processTime: this.processTime,
      residenceTime: flow.residenceTime,
      dissociationFraction,
      residual: this.residual,
    }
  }

  /**
   * Run a throwaway copy of the process to a given time under a modified recipe.
   * This is what lets the assistant answer "what if" without disturbing the
   * wafer the student has in the chamber.
   */
  static predict(
    base: { recipe: Recipe; chamber: ChamberState },
    patch: Partial<Recipe>,
    seconds: number,
    dt = 2,
    /**
     * Counterfactual chamber condition. This is what lets a diagnosis be a
     * real experiment: restore one suspect reading to nominal, leave everything
     * else alone, and see whether the symptom goes away.
     */
    chamberPatch: Partial<ChamberState> = {},
  ): { metrics: EtchMetrics; samples: RadialSample[] } {
    const sim = new EtchSim()
    sim.chamber = { ...base.chamber, ...chamberPatch }
    sim.recipe = { ...base.recipe, ...patch }
    sim.loadWafer()
    const steps = Math.max(1, Math.round(seconds / dt))
    for (let s = 0; s < steps; s++) sim.step(dt)
    return { metrics: sim.metrics(), samples: sim.samples() }
  }
}
